// SVG lockup for the "AIghtBet Sports" wordmark — web counterpart to the
// react-native-svg port at `apps/ios/components/brand/Wordmark.tsx`. Same
// viewBox, same primitive positions, same brand color treatment, so the
// lockup renders identically on aightbetsports.com (desktop + mobile web)
// and inside the iOS app.
//
// Bug class this component eliminates: the system display font (Geist,
// Inter, IBM Plex Sans, SF Pro) renders capital `I` as a featureless
// vertical bar — visually identical to lowercase `l` — so a text-based
// "AIghtBet" reads as "AlghtBet" on every brand-anchor surface. The fix
// renders the capital `I` as three SVG rect primitives (top serif +
// vertical bar + bottom serif) so the glyph is NEVER subject to font
// rendering. Brand color treatment ("AI" + "Bet" in brand orange, "ght"
// in fg, " Sports" in fg-muted) further reinforces the parse.
//
// Visual parity rule: any change to letter positions, weights, colors, or
// the I-beam geometry MUST be mirrored in the iOS sibling file. The two
// components are the single brand wordmark surface and must render the
// same pixels.

import React from 'react';

const BRAND = '#FB923C';
const FG = '#F5F5F5';
const FG_MUTED = '#A1A1AA';

const VB_WIDTH = 224;
const VB_HEIGHT = 40;

const FONT_DISPLAY =
  'Geist, "IBM Plex Sans", Inter, "Inter Display", -apple-system, BlinkMacSystemFont, system-ui, sans-serif';

interface WordmarkProps {
  /** Rendered height in CSS pixels. Width scales proportionally (5.6:1). */
  height?: number;
  /** Additional className for layout (margins, etc.). */
  className?: string;
  /** Override the title rendered for screen readers. Defaults to
   *  "AIghtBet Sports" — set to empty string when paired with an
   *  adjacent heading that already announces the brand. */
  title?: string;
  /** Hide from the a11y tree entirely (decorative use). */
  decorative?: boolean;
}

export const Wordmark: React.FC<WordmarkProps> = ({
  height = 32,
  className,
  title = 'AIghtBet Sports',
  decorative = false,
}) => {
  const width = (height * VB_WIDTH) / VB_HEIGHT;
  const a11yProps = decorative
    ? { 'aria-hidden': true as const, role: 'presentation' as const }
    : { role: 'img' as const, 'aria-label': title };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
      className={className}
      {...a11yProps}
    >
      {!decorative && <title>{title}</title>}

      {/* "A" — brand orange. Baseline y=32 inside a 40-unit viewBox. */}
      <text
        x={0}
        y={32}
        fontFamily={FONT_DISPLAY}
        fontSize={32}
        fontWeight={800}
        fill={BRAND}
      >
        A
      </text>

      {/* Capital I — hand-drawn I-beam (top serif + vertical + bottom
          serif). This is the disambiguation: never rendered as text, so
          never collapses to a featureless vertical bar regardless of
          which fallback font the platform picks. */}
      <g fill={BRAND}>
        <rect x={26} y={8} width={16} height={3} />
        <rect x={31} y={8} width={6} height={24} />
        <rect x={26} y={29} width={16} height={3} />
      </g>

      {/* "ght" — fg white. */}
      <text
        x={44}
        y={32}
        fontFamily={FONT_DISPLAY}
        fontSize={32}
        fontWeight={800}
        fill={FG}
      >
        ght
      </text>

      {/* "Bet" — brand orange, italic. Mirrors the desktop Login.tsx
          stylization ("AIght" + italic-orange "Bet" + muted "Sports"). */}
      <text
        x={104}
        y={32}
        fontFamily={FONT_DISPLAY}
        fontSize={32}
        fontWeight={800}
        fontStyle="italic"
        fill={BRAND}
      >
        Bet
      </text>

      {/* " Sports" — muted, 70% cap height, 400 weight. */}
      <text
        x={156}
        y={32}
        fontFamily={FONT_DISPLAY}
        fontSize={22}
        fontWeight={400}
        fill={FG_MUTED}
      >
        Sports
      </text>
    </svg>
  );
};

export default Wordmark;
